import { SectionHeading } from '~/components/ui/SectionHeading'
import { createFileRoute } from '@tanstack/react-router'
import { pageMeta } from '~/lib/site/seo'
import { cacheHeaders } from '~/lib/site/cache'
import { Container } from '~/components/ui/Container'
import { FaqAccordion } from '~/components/sections/FaqAccordion'
import { DonateSignupBand } from '~/components/sections/DonateSignupBand'
import { Img } from '~/components/ui/Img'
import community from '~/assets/images/new-chapter-community.jpg?responsive'
// Same photo as the homepage hero; one file, or the client and server builds
// can pick different names for the identical asset.
import directAction from '~/assets/images/hero-march.jpg?responsive'
import { PageTitle } from '~/components/ui/PageTitle'
import { PageHero } from '~/components/ui/PageHero'

export const Route = createFileRoute('/new-chapter')({
  headers: () => cacheHeaders.content,
  head: () =>
    pageMeta({
      title: 'Create a local chapter',
      description:
        'Steps to create a local DxE chapter: build a strong community and organize direct action in your area.',
      ogDescription: 'Steps to create a local DxE chapter.',
    }),
  component: NewChapter,
})

const pillars = [
  {
    img: community,
    title: 'Strong community',
    body: 'Having a dense concentration of activists that support each other makes the work more sustainable and keeps people involved.',
  },
  {
    img: directAction,
    title: 'Direct action',
    body: 'It is critical to challenge systems of oppression directly and consistently through the power of nonviolent direct action.',
  },
]

const steps = [
  {
    question: '1. Fill the form at dxe.io/join',
    answer: (
      <p>
        DxE’s mentoring team is here to help! Visit{' '}
        <a href="https://dxe.io/join" target="_blank" rel="noreferrer">
          dxe.io/join
        </a>{' '}
        and let us know where you&apos;re located.
      </p>
    ),
  },
  {
    question: '2. Learn the theory of change behind DxE',
    answer: (
      <p>
        We will break down DxE&apos;s theory of change for you via video call
        and answer any questions you have.
      </p>
    ),
  },
  {
    question: '3. Organize events that activate your community',
    answer: (
      <p>
        Then you can put what you&apos;ve learned into practice. Plan a potluck
        or a protest -- or even both on the same day!
      </p>
    ),
  },
  {
    question:
      '4. Participate in a monthly video call with all chapter organizers',
    answer: (
      <p>
        On the first Sunday of every month, DxE organizers from around the world
        come together via video call to share updates and discuss the latest
        campaigns.
      </p>
    ),
  },
]

function NewChapter() {
  return (
    <>
      <PageHero bottom="section">
        <Container width="narrow">
          <PageTitle className="mb-5 text-center">
            Create a local chapter
          </PageTitle>
          <p className="mt-2 text-center text-base leading-6">
            All successful social movements have two things in common:
          </p>
        </Container>
        <Container className="pt-10">
          <div className="grid gap-6 md:grid-cols-2">
            {pillars.map((p) => (
              <div
                key={p.title}
                className="border border-fg/10 bg-surface shadow-[7px_7px_60px_var(--color-card-shadow)]"
              >
                <div className="relative h-[250px] overflow-hidden lg:h-[350px]">
                  <Img
                    image={p.img}
                    sizes="(min-width: 768px) 45vw, 90vw"
                    alt={p.title}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h2 className="mb-2.5 text-[32px] leading-[38px] font-bold text-fg">
                    {p.title}
                  </h2>
                  <p className="mb-0 text-base leading-6">{p.body}</p>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </PageHero>

      <section className="pb-10">
        <Container className="max-w-[900px]">
          <SectionHeading>Steps to create a local chapter</SectionHeading>
          <FaqAccordion items={steps} />
        </Container>
      </section>

      <DonateSignupBand showDonate={false} signupTitle="Join our network" />
    </>
  )
}
